import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { readSheet } from 'read-excel-file/browser';
import { environment } from '../../../../../../../environments/environment';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { TipoContatoEnum } from '@shared/enums/pessoa/tipo-contato.enum';
import { AcaoAlteracaoProdutoEnum } from '@shared/enums/compras/acao-alteracao-produto.enum';
import { CriterioCotacaoEnum } from '@shared/enums/compras/criterio-cotacao.enum';
import { OrigemCotacaoEnum } from '@shared/enums/compras/origem-cotacao.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { AutomacaoService } from '@shared/services/apis/compras/automacao.service';
import { CompraCotacaoService } from '@shared/services/apis/compras/compra-cotacao.service';
import { FormaPagamentoService } from '@shared/services/apis/financas/forma-pagamento.service';
import { FormaPagamento } from '@shared/services/models/financas/forma-pagamento.model';
import { PessoaLookupService } from '@shared/services/apis/pessoa/pessoa-lookup.service';
import { PortalCotacaoService } from '@shared/services/apis/compras/portal-cotacao.service';
import { AtualizarCotacaoFornecedorParcelaRequest } from '@shared/services/requests/compras/atualizar-cotacao-fornecedor-parcela.request';
import { AtualizarCotacaoFornecedorRequest } from '@shared/services/requests/compras/atualizar-cotacao-fornecedor.request';
import { TipoAutomacaoEnum } from '@shared/enums/compras/tipo-automacao.enum';
import { TipoOcorrenciaCompraEnum } from '@shared/enums/compras/tipo-ocorrencia-compra.enum';
import { CotacaoFornecedorItemStruct } from '@shared/services/structs/compras/cotacao-fornecedor-item.struct';
import { CotacaoFornecedorStruct } from '@shared/services/structs/compras/cotacao-fornecedor.struct';
import { TipoFreteStruct } from '@shared/services/structs/compras/tipo-frete.struct';
import { ComparisonMatrixDialogComponent, ComparisonMatrixResult, MatrixOffer, MatrixProduct, MatrixSupplier, MatrixSupplierEdit, MatrixSupplierHeader } from '../../comparison-matrix-dialog/comparison-matrix-dialog.component';
import { CotacaoParcelasEditorComponent } from '../../cotacao-parcelas-editor/cotacao-parcelas-editor.component';
import { EnviarCotacaoEmailDialogComponent } from '../../enviar-cotacao-email-dialog/enviar-cotacao-email-dialog.component';
import { SolicitarCotacaoDialogComponent, SolicitarCotacaoFornecedor } from '../../solicitar-cotacao-dialog/solicitar-cotacao-dialog.component';
import type { KanbanCard } from '../../../processo-compras.models';
import { CedProdutosComponent } from '../panels/ced-produtos.component';
import {
  avatarColorFromSeed,
  calcularSelecaoCotacao,
  CardProduct,
  CotacaoParcela,
  CotacaoParcelasState,
  CotacaoSelectionItem,
  CotacaoSupplier,
  estadoParcelasDaCotacao,
  inicioDoDia,
  initialsFromName,
  mapCotacaoFornecedor,
  mapParcelasCotacao,
  mensagemAlteracaoProdutoCotacao,
  nomeFornecedorFallback,
  numToBR,
  formatKanbanCardValue,
  parcelasParaRequest,
} from '../card-edit-dialog.types';

/**
 * Etapa de Cotação: produtos (painel compartilhado app-ced-produtos), fornecedores convidados
 * (parcelas/envio/salvar), fluxo offline por planilha, pedido montado (seleção vencedora +
 * matriz) e justificativa da escolha.
 *
 * Contrato com o shell:
 * - `card` e `products` são referências do shell — persistirSelecaoCotacao reflete os preços
 *   nos produtos e o total em `card.value`, como antes da extração.
 * - Produtos incluídos/removidos/editados aqui saem por `(productsChange)`; o shell guarda a lista
 *   (enviada no doSave) e rebinda `[products]`. Produto novo só ganha idCompraItem ao salvar —
 *   até lá fica fora da matriz, da seleção e da planilha (todas casam por idCompraItem).
 * - `(dirty)` marca alterações não salvas; `(savingChange)` espelha o spinner de salvamento
 *   do cabeçalho durante a persistência das edições da matriz.
 * - O shell chama `persistirSelecaoCotacao()` via @ViewChild ao avançar Cotação → Pedido
 *   (o filho está garantidamente vivo, pois a etapa atual é 'cotacao').
 */
@Component({
  selector: 'app-ced-stage-cotacao',
  standalone: true,
  imports: [
    FormsModule,
    AlertCardComponent,
    CotacaoParcelasEditorComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    SelectPesquisavelComponent,
    CedProdutosComponent,
  ],
  templateUrl: './ced-stage-cotacao.component.html',
  styleUrls: ['../ced-scrollbars.css', './ced-stage-cotacao.component.css', '../ced-painel-inclusao.css'],
})
export class CedStageCotacaoComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) card!: KanbanCard;
  @Input() products: CardProduct[] = [];
  /** Critério persistido em compra.idCriterioCotacao — carregado pelo shell. */
  @Input() criterioInicial: CriterioCotacaoEnum | null = null;
  /** Justificativa da escolha persistida em compra.justificativaCotacao — carregada pelo shell. */
  @Input() justificativa = '';
  /** Cotação arquivada: já gerou os pedidos e não aceita mais convite, envio, importação nem matriz. */
  @Input() somenteLeitura = false;

  /** Alterações não salvas (matriz aplicada, justificativa digitada…). */
  @Output() dirty = new EventEmitter<void>();
  /** Espelha o estado de salvamento no shell (spinner do botão "Salvar e fechar"). */
  @Output() savingChange = new EventEmitter<boolean>();
  /** Critério escolhido — o shell guarda para reenviar no doSave (preserva entre etapas). */
  @Output() criterioChange = new EventEmitter<CriterioCotacaoEnum>();
  /** Justificativa da escolha — o shell guarda para reenviar no doSave (preserva entre etapas). */
  @Output() justificativaChange = new EventEmitter<string>();
  /** Lista de produtos alterada no painel — o shell guarda e envia no doSave. */
  @Output() productsChange = new EventEmitter<CardProduct[]>();

  cotSuppliers: CotacaoSupplier[] = [];
  /**
   * Cotações enviadas cujo link foi (ou será, ao salvar) desativado pela inclusão/remoção de
   * produto, por id do CotacaoFornecedor → texto de orientação (tooltip do selo "Desatualizada"
   * e nota da linha). Ver recalcularDesatualizadas.
   */
  cotDesatualizada: Record<string, string> = {};
  /**
   * idCompraItem dos produtos quando as cotações chegaram do servidor. Base para saber se esta
   * edição incluiu ou removeu produto (ver cotProdutosAlterados); renovada a cada recarga bem-sucedida.
   */
  private cotIdsCompraItemCarregados = new Set<number>();
  cotAdding = false;
  /** POST do convite em andamento — o select fica bloqueado até a cotação existir no servidor. */
  cotAdicionando = false;
  cotLoading = false;
  /** URL do lookup padrão de fornecedor, usada pelo app-select-pesquisavel do convite. */
  public urlFornecedor = `${environment.urlApiPessoa}Lookup/listPessoaSelect?idTipoContato=${TipoContatoEnum.Fornecedor}`;
  fornecedorControl = new FormControl<number | null>(null);
  expandedSupplierId: string | null = null;
  /** Id do fornecedor cuja cotação está sendo salva+enviada (loader no botão). */
  cotSendingId: string | null = null;
  cotHasSelection = false;
  cotSelection: CotacaoSelectionItem[] = [];
  /** Seleção aplicada pela matriz: passa a ser a decisão vigente e não é mais recalculada. */
  private cotSelecaoAplicadaNaMatriz = false;
  /** Critério vigente — escolhido na matriz de comparação; o "Pedido montado" só exibe o resultado. */
  cotCriterio: CriterioCotacaoEnum = CriterioCotacaoEnum.MenorPrecoTotal;
  readonly OrigemCotacaoEnum = OrigemCotacaoEnum;
  /** Data-base única da sessão para manter dias e vencimento sempre sincronizados. */
  public readonly dataBaseParcelas = inicioDoDia(new Date());
  /** Tipos de frete para o painel de edição da matriz — lista de referência, carregada uma vez. */
  private tiposFrete: TipoFreteStruct[] = [];
  /** Formas de pagamento (cadastro de Finanças) do select das parcelas — carregadas uma vez. */
  formasPagamento: FormaPagamento[] = [];
  /** Editor de parcelas por fornecedor (chave = id do CotacaoFornecedor). */
  cotParcelasState: Record<string, CotacaoParcelasState> = {};
  private cotMeiosPagamentoAlterados: Record<string, boolean> = {};
  private cotacoes: CotacaoFornecedorStruct[] = [];
  /**
   * Nomes/dados de fornecedor resolvidos por idFornecedor. O backend não guarda o nome
   * (vem do cadastro de pessoas), então cacheamos aqui para reaplicar a cada aplicarCotacoes()
   * — sem isso, adicionar/remover um fornecedor faria os demais voltarem ao fallback "Fornecedor {id}".
   */
  // `carregado` distingue "fornecedor sem e-mail" de "cadastro que não conseguimos ler": o
  // convite semeia o nome pelo select, o e-mail só vem do lookup de Pessoas.
  private nomesFornecedor = new Map<number, { name: string; initials: string; email?: string | null; documento?: string | null; carregado: boolean }>();

  // Cotação — planilha (fluxo offline)
  cotSheetOpen = false;
  cotSheetSupplierId = '';
  cotSheetFileName = '';
  private cotSheetFile: File | null = null;
  private readonly cotSheetMaxSizeMb = 5;
  /** Leitura do arquivo + gravação da resposta importada em andamento (spinner do botão Importar). */
  cotImportando = false;
  @ViewChild('cotSheetInput') cotSheetInput?: ElementRef<HTMLInputElement>;

  private readonly subs = new Subscription();
  /** Serializa escritas: cancelar a Subscription HTTP não cancela uma gravação já iniciada no servidor. */
  private cotPersistInFlight = false;
  private cotPersistPending?: () => void;

  constructor(
    private compraCotacaoService: CompraCotacaoService,
    private formaPagamentoService: FormaPagamentoService,
    private automacaoService: AutomacaoService,
    private pessoaLookupService: PessoaLookupService,
    private portalCotacaoService: PortalCotacaoService,
    private alertService: AlertService,
    private dialog: MatDialog,
  ) { }

  /** Qualquer edição no editor de parcelas marca as condições como alteradas e suja o card. */
  public cotMarcarMeioPagamentoAlterado(idCotacaoFornecedor: string): void {
    this.cotMeiosPagamentoAlterados[idCotacaoFornecedor] = true;
    this.dirty.emit();
  }

  public cotCondicoesPagamentoAlteradas(s: CotacaoSupplier): boolean {
    const estado = this.cotParcelasState[s.id];
    const cotacao = this.cotacoes.find(item => item.idCotacaoFornecedor === s.idCotacaoFornecedor);
    if (!estado || !cotacao) return false;

    if (estado.expr.trim() !== (cotacao.condicaoPagamento ?? '').trim()) return true;
    if (this.cotMeiosPagamentoAlterados[s.id]) return true;

    const parcelasPersistidas = mapParcelasCotacao(this.dataBaseParcelas, cotacao.parcelas);
    return estado.parcelas.length !== parcelasPersistidas.length
      || estado.parcelas.some((parcela, index) => parcela.dias !== parcelasPersistidas[index]?.dias);
  }

  ngOnInit(): void {
    if (this.criterioInicial != null) {
      this.cotCriterio = this.criterioInicial;
    }
    this.carregarCotacoes();
    this.carregarTiposFrete();
    this.carregarFormasPagamento();
  }

  /**
   * O shell rebinda `[products]` com o idCompraItem dos produtos novos depois do save; se as
   * cotações recarregadas chegarem antes desse rebind, o selo ficaria com "Os produtos mudaram".
   * Sem cotações carregadas o mapa sai vazio.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['products']) this.recalcularDesatualizadas();
  }

  /**
   * Tipos de frete para o painel da matriz. O endpoint é o do portal público (anônimo) — única
   * fonte hoje; falhar aqui só deixa o select vazio, sem bloquear os outros campos.
   */
  private carregarTiposFrete(): void {
    this.subs.add(
      this.portalCotacaoService.tiposFrete().subscribe({
        next: (res) => { if (!res.isError) this.tiposFrete = res.lista || []; },
        error: () => { /* select fica vazio; demais campos seguem editáveis */ },
      }),
    );
  }

  /**
   * Formas de pagamento do select das parcelas (etapa e matriz). Só as ativas são oferecidas;
   * a parcela grava o idFormaPagamento escolhido.
   * O lookup exige permissão de configurações de Finanças: a falha é silenciosa e, com a lista
   * vazia, o select fica oculto. Lookup sem essa permissão fica como pendência (dossiê UC-08).
   */
  private carregarFormasPagamento(): void {
    this.subs.add(
      this.formaPagamentoService.get().subscribe({
        next: (res) => {
          if (res.isError) return;
          this.formasPagamento = (res.listaFormaPagamento || []).filter(f => f.isAtivo);
        },
        error: () => { /* sem permissão ou indisponível: select de forma de pagamento oculto */ },
      }),
    );
  }

  /** Notifica o shell do critério atual (persistido em compra.idCriterioCotacao no próximo save). */
  private emitirCriterio(): void {
    this.criterioChange.emit(this.cotCriterio);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private carregarCotacoes(): void {
    if (!this.card.idCompra) return;

    this.cotLoading = true;
    this.subs.add(
      this.compraCotacaoService.listarCotacoes(this.card.idCompra).subscribe({
        next: (res) => {
          this.cotLoading = false;
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível carregar as cotações.', AlertType.error);
            return;
          }

          // Snapshot na chegada, não na chamada: após o PUT, o rebind de [products] já trouxe os ids novos.
          this.cotIdsCompraItemCarregados = new Set(this.products.map(p => p.idCompraItem).filter((id): id is number => id != null));
          this.cotacoes = res.lista || [];
          this.aplicarCotacoes();
          this.carregarNomesFornecedores();
        },
        error: () => {
          this.cotLoading = false;
          this.alertService.show('Erro', 'Não foi possível carregar as cotações.', AlertType.error);
        },
      }),
    );
  }

  private aplicarCotacoes(): void {
    this.cotSuppliers = this.cotacoes.map(c => {
      const base = mapCotacaoFornecedor(c);
      const info = this.nomesFornecedor.get(c.idFornecedor);
      if (!info) return base;
      return {
        ...base,
        name: info.name,
        initials: info.initials,
        email: info.email ?? base.email,
        documento: info.documento ?? base.documento,
      };
    });
    this.recalcularSelecaoCotacao();
    this.recalcularDesatualizadas();
  }

  /**
   * Esta edição incluiu (produto ainda sem idCompraItem) ou removeu (id carregado que sumiu)
   * produto. Quantidade não conta. Zera quando as cotações são recarregadas depois do save
   * (persistirCotacoes) e o shell preenche o idCompraItem dos produtos novos.
   */
  private cotProdutosAlterados(): boolean {
    if (this.products.some(p => !p.idCompraItem)) return true;
    const atuais = new Set(this.products.map(p => p.idCompraItem));
    return [...this.cotIdsCompraItemCarregados].some(id => !atuais.has(id));
  }

  /**
   * Selo "Desatualizada": incluir ou remover produto faz o backend desativar, ao salvar, o link
   * de toda cotação enviada; o próximo envio gera link novo e reabre o portal para quem já respondeu.
   * - Link já desativado (isLinkDesativado): falta reenviar.
   * - Link ativo e produtos alterados nesta edição: será desativado ao salvar.
   * - Respondida sem envio (matriz/planilha): não há link, mas a proposta fica sem o produto
   *   novo. Pendente pelos produtos alterados; depois de salvar, pelo produto sem linha na
   *   cotação (as linhas nascem no convite e na resposta, então faltar linha = entrou depois).
   *   Já reenviada aguardando a nova resposta não marca: o reenvio já foi feito.
   * - Não enviada nem respondida: nunca.
   */
  private recalcularDesatualizadas(): void {
    const mapa: Record<string, string> = {};
    const alterados = this.cotProdutosAlterados();
    for (const c of this.cotacoes) {
      const id = String(c.idCotacaoFornecedor);
      if (c.datetimeEnvio) {
        if (c.isLinkDesativado) {
          mapa[id] = c.datetimeResposta
            ? 'O link enviado foi desativado porque os produtos mudaram. Reenvie a cotação para gerar um novo link; o fornecedor poderá completar a proposta com os preços anteriores já preenchidos.'
            : 'O link enviado foi desativado porque os produtos mudaram. Reenvie a cotação para gerar um novo link.';
        } else if (alterados) {
          mapa[id] = 'Os produtos mudaram. Ao salvar, o link enviado será desativado; reenvie a cotação depois de salvar.';
        }
        continue;
      }
      if (!c.datetimeResposta) continue;
      const comLinha = new Set((c.itens || []).map(i => i.idCompraItem));
      const semProposta = this.products.filter(p => p.idCompraItem && !comLinha.has(p.idCompraItem));
      if (alterados) {
        mapa[id] = 'Os produtos mudaram e a proposta deste fornecedor não cobre a nova lista. Depois de salvar, envie a cotação para ele completar (os preços anteriores vêm preenchidos) ou registre os preços pela matriz ou por planilha.';
      } else if (semProposta.length) {
        const nomes = semProposta.map(p => p.name);
        const lista = nomes.length <= 1 ? nomes[0] : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
        mapa[id] = `A proposta não inclui ${lista}. Envie a cotação para o fornecedor completar (os preços anteriores vêm preenchidos) ou registre os preços pela matriz ou por planilha.`;
      }
    }
    this.cotDesatualizada = mapa;
  }

  private cotNomeFornecedor(c: CotacaoFornecedorStruct): string {
    return this.cotSuppliers.find(s => s.idCotacaoFornecedor === c.idCotacaoFornecedor)?.name
      || nomeFornecedorFallback(c.idFornecedor);
  }

  /**
   * Lista vinda do painel de produtos (a inclusão/remoção já foi confirmada no painel).
   * Repassa ao shell, ajusta a seleção ao que restou e recalcula o selo "Desatualizada".
   */
  cotOnProductsChange(lista: CardProduct[]): void {
    this.products = lista;

    // A seleção aplicada pela matriz (ou manual) não é recalculada: só sai o produto removido. A
    // quantidade segue a "pedir" da cotação (cotacao_fornecedor_item.quantidade), não a solicitada.
    const ids = new Set(lista.map(p => p.idCompraItem).filter((id): id is number => id != null));
    this.cotSelection = this.cotSelection.filter(s => s.idCompraItem == null || ids.has(s.idCompraItem));
    this.recalcularSelecaoCotacao();
    this.recalcularDesatualizadas();
    this.productsChange.emit(lista);
  }

  /** Confirmação do painel ao incluir/remover produto com cotação já enviada ou respondida. */
  cotAvisoAlteracaoProduto = (acao: AcaoAlteracaoProdutoEnum, p: CardProduct): string | null =>
    mensagemAlteracaoProdutoCotacao(acao, p, this.cotacoes, c => this.cotNomeFornecedor(c));

  private carregarNomesFornecedores(aposResolver?: () => void): void {
    const ids = [...new Set(this.cotacoes.map(c => c.idFornecedor).filter(Boolean))];
    if (!ids.length) return;

    this.subs.add(
      this.pessoaLookupService.getListPessoaIds({ listIdPessoa: ids }).subscribe({
        next: (res) => {
          if (res.isError) return;
          for (const p of res.listaPessoa || []) {
            const name = p.nome || p.nomeFantasia || nomeFornecedorFallback(p.idPessoa);
            this.nomesFornecedor.set(p.idPessoa, {
              name,
              initials: initialsFromName(name),
              email: p.emailNFe,
              documento: p.cnpj || p.cpf,
              carregado: true,
            });
          }
          this.aplicarCotacoes();
          aposResolver?.();
        },
      }),
    );
  }

  private recalcularSelecaoCotacao(): void {
    // Não recalcula por cima de uma decisão do comprador: seleção já aplicada na matriz, ou
    // critério "Definido manualmente pelo comprador". Nesses casos o critério serve apenas
    // como sugestão dentro da matriz.
    if (this.cotSelecaoAplicadaNaMatriz || this.cotCriterio === CriterioCotacaoEnum.DefinidoManualmente) {
      this.cotHasSelection = this.cotSelection.length > 0;
      return;
    }

    const selecionados = calcularSelecaoCotacao(this.cotacoes, this.products, this.cotSuppliers, this.cotCriterio);
    this.cotSelection = selecionados;
    this.cotHasSelection = selecionados.length > 0;
  }

  /** Completa a seleção vinda da matriz com idCompraItem/valorUnitário/idFornecedor para persistência. */
  private enriquecerSelecaoDaMatriz(selection: CotacaoSelectionItem[]): CotacaoSelectionItem[] {
    return selection.map(s => {
      // Casa por idCompraItem; o match por sku é só fallback para seleções antigas sem o id.
      // Produtos sem SKU (avulso) têm sku vazio e casavam entre si — a seleção chegava sem
      // idCompraItem, era filtrada em persistirSelecaoCotacao e nada era gravado.
      const produto = this.products.find(p => s.idCompraItem != null && p.idCompraItem === s.idCompraItem)
        ?? this.products.find(p => !!s.sku && (p.sku || String(p.idCompraItem)) === s.sku);
      // Reconcilia por idFornecedor (identidade real), não pelo nome exibido — dois fornecedores
      // convidados podem ter o mesmo nome, e um match por string escolheria o primeiro arbitrariamente.
      const supplier = this.cotSuppliers.find(sup => sup.idFornecedor === s.idFornecedor)
        ?? this.cotSuppliers.find(sup => sup.name === s.supplier);
      const valorUnitario = s.valorUnitario ?? (s.qty ? s.subtotal / s.qty : null);
      // Quantidade do pedido = "pedir" devolvida pela matriz (cotacao_fornecedor_item.quantidade do
      // vencedor, gravada no backend) — a mesma base do modo automático. A etapa não a sobrescreve
      // com a quantidade solicitada.
      const qtd = s.qty;
      return {
        ...s,
        qty: qtd,
        subtotal: valorUnitario != null ? valorUnitario * qtd : s.subtotal,
        idCompraItem: produto?.idCompraItem ?? s.idCompraItem ?? null,
        valorUnitarioSel: valorUnitario,
        idFornecedorSel: supplier?.idFornecedor ?? s.idFornecedorSel ?? null,
      };
    });
  }

  /**
   * Aplica a seleção vencedora à compra: grava o preço unitário escolhido em cada item e o fornecedor,
   * recalculando o valor total no backend. Reflete os preços nos produtos e o total no card.
   * Público: o shell chama via @ViewChild ao avançar Cotação → Pedido (safety net).
   */
  persistirSelecaoCotacao(afterApply?: () => void): void {
    const itens = this.cotSelection
      .filter(s => s.idCompraItem != null)
      .map(s => ({
        idCompraItem: s.idCompraItem!,
        valorUnitario: s.valorUnitarioSel ?? (s.qty ? s.subtotal / s.qty : null),
        quantidade: s.qty || null,
        idFornecedor: s.idFornecedorSel ?? null,
      }));

    if (!this.card.idCompra || itens.length === 0) { afterApply?.(); return; }

    // Fornecedor da compra: único vencedor (pedido com 1 fornecedor) — split deixa em branco.
    const fornecedores = [...new Set(this.cotSelection.map(s => s.idFornecedorSel).filter((v): v is number => v != null))];
    const idFornecedor = fornecedores.length === 1 ? fornecedores[0] : null;

    if (this.cotPersistInFlight) {
      this.cotPersistPending = () => this.persistirSelecaoCotacao(afterApply);
      return;
    }

    this.cotPersistInFlight = true;
    const finalizar = () => {
      this.cotPersistInFlight = false;
      const pendente = this.cotPersistPending;
      this.cotPersistPending = undefined;
      pendente?.();
    };

    const persistSub = this.compraCotacaoService.aplicarSelecaoCotacao(this.card.idCompra, { idFornecedor, itens }).subscribe({
      next: (res) => {
        if (!res.isError) {
          // Reflete o preço nos itens locais. A quantidade do painel de Produtos continua a
          // solicitada (dona: Solicitação); a do pedido é a "pedir" enviada em `itens`.
          for (const sel of itens) {
            const p = this.products.find(x => x.idCompraItem === sel.idCompraItem);
            if (p) {
              p.valorUnitario = sel.valorUnitario ?? null;
              p.price = numToBR(sel.valorUnitario);
            }
          }
          if (res.valorTotal != null || this.cotSelectionTotal > 0) {
            this.card.value = formatKanbanCardValue(res.valorTotal, this.cotSelectionTotal);
          }
          afterApply?.();
        } else {
          this.alertService.show('Erro', res.errorDescription || 'Não foi possível aplicar a seleção da cotação.', AlertType.error);
        }
      },
      error: () => {
        this.alertService.show('Erro', 'Não foi possível aplicar a seleção da cotação.', AlertType.error);
        finalizar();
      },
      complete: finalizar,
    });
    this.subs.add(persistSub);
  }

  get cotReceived(): number {
    return this.cotSuppliers.filter(s => s.status === 'recebida').length;
  }

  get cotPending(): number {
    return this.cotSuppliers.length - this.cotReceived;
  }

  get cotAllReceived(): boolean {
    return this.cotPending === 0;
  }

  get cotSelectionTotal(): number {
    return this.cotSelection.reduce((sum, it) => sum + it.subtotal, 0);
  }

  get cotUniqueSuppliers(): string[] {
    return [...new Set(this.cotSelection.map(it => it.supplier))];
  }

  cotUnitPrice(it: CotacaoSelectionItem): number {
    return it.qty ? it.subtotal / it.qty : 0;
  }

  cotFmt(n: number): string {
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /** Fornecedores com convite ativo: não aparecem na lista de "Adicionar fornecedor". */
  get cotIdsFornecedoresConvidados(): number[] {
    return this.cotSuppliers.map(s => s.idFornecedor);
  }

  cotToggleAdding(): void {
    this.cotAdding = !this.cotAdding;
    if (!this.cotAdding) {
      this.fornecedorControl.reset();
    }
  }

  /**
   * Grava o convite na hora. A matriz, o link e o envio dependem do idCotacaoFornecedor real;
   * esperar o "Salvar e fechar" deixava o fornecedor fora da comparação.
   * O e-mail continua sendo só sugerido no save do card.
   */
  cotAddSupplier(sel: { id: number; descricao: string; codigo?: string } | null): void {
    if (!sel || !this.card.idCompra || this.cotAdicionando) return;

    if (this.cotSuppliers.some(s => s.idFornecedor === sel.id)) {
      this.alertService.show('Atenção', 'Este fornecedor já está na cotação.', AlertType.warning);
      this.fornecedorControl.reset();
      return;
    }

    const idCompra = this.card.idCompra;
    this.cotAdicionando = true;
    this.subs.add(
      this.compraCotacaoService.adicionarCotacaoFornecedor(idCompra, { idFornecedor: sel.id }).subscribe({
        next: (res) => {
          this.cotAdicionando = false;
          if (res.isError || !res.cotacao) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível adicionar o fornecedor.', AlertType.error);
            this.fornecedorControl.reset();
            return;
          }

          if (this.cotacoes.some(c => c.idCotacaoFornecedor === res.cotacao!.idCotacaoFornecedor)) {
            this.alertService.show('Atenção', 'Este fornecedor já está na cotação.', AlertType.warning);
            this.fornecedorControl.reset();
            return;
          }

          // Semeia o cache de nomes com os dados do select para o fornecedor recém-convidado
          // aparecer com o nome correto imediatamente (aplicarCotacoes reaplica em todos).
          this.nomesFornecedor.set(sel.id, {
            name: sel.descricao,
            initials: initialsFromName(sel.descricao),
            documento: sel.codigo || null,
            carregado: false,
          });
          this.cotacoes = [...this.cotacoes, res.cotacao];
          this.aplicarCotacoes();
          // O select não traz e-mail; busca o emailNFe (e nome canônico) do cadastro agora para que a
          // automação de envio, disparada no save, já encontre o destinatário.
          this.carregarNomesFornecedores();
          this.expandedSupplierId = String(res.cotacao.idCotacaoFornecedor);
          this.cotEnsureParcelas(this.expandedSupplierId);
          this.cotAdding = false;
          this.fornecedorControl.reset();
        },
        error: () => {
          this.cotAdicionando = false;
          this.fornecedorControl.reset();
          this.alertService.show('Erro', 'Não foi possível adicionar o fornecedor.', AlertType.error);
        },
      }),
    );
  }

  /**
   * Grava tudo que a etapa de cotação mantém fora de AtualizarCompraRequest, na ordem em que
   * depende uma da outra: condições de pagamento → solicitação de envio.
   * O shell chama após salvar a compra (o e-mail é gerado do que está persistido).
   *
   * `sugerirEnvio` é falso quando o save é parte de uma movimentação de etapa: o card está
   * deixando a cotação e sugerir o formulário ali seria pedir cotação de quem já não vai cotar.
   */
  public persistirCotacoes(onDone: () => void, onError?: () => void, sugerirEnvio = true): void {
    const idCompra = this.card.idCompra;
    if (!idCompra) { onDone(); return; }

    // Incluir/remover produto faz o PUT da compra desativar os links enviados (e apagar o item
    // removido das cotações): recarrega para o selo refletir isLinkDesativado e renovar a base
    // de cotProdutosAlterados.
    const recarregar = this.cotProdutosAlterados();
    const concluir = () => {
      if (recarregar) this.carregarCotacoes();
      onDone();
    };

    this.salvarCondicoesPagamento(
      idCompra,
      () => sugerirEnvio ? this.sugerirSolicitacaoCotacao(idCompra, concluir, recarregar) : concluir(),
      onError,
    );
  }

  /**
   * Automação "Solicitação de envio de formulário de cotação para fornecedor": ao salvar o
   * card, sugere enviar para quem ainda não recebeu e pede confirmação num modal. Não envia
   * sozinha — o e-mail é irreversível, e o comprador pode ter convidado alguém só para
   * organizar a cotação. O e-mail vem do cadastro de Pessoas (this.nomesFornecedor), por isso
   * isto mora aqui e não no backend; falha no envio não invalida o save, só alerta.
   * Também sugere o reenvio para quem teve o link desativado pela inclusão/remoção de produto:
   * `produtosAlterados` cobre este save, porque as cotações só são recarregadas (com
   * isLinkDesativado) depois daqui.
   */
  private sugerirSolicitacaoCotacao(idCompra: number, onDone: () => void, produtosAlterados: boolean): void {
    this.subs.add(
      this.automacaoService.automacaoAtiva(TipoAutomacaoEnum.EnviarCotacaoFornecedor).subscribe({
        next: (automacao) => {
          if (!automacao) { onDone(); return; }

          // Todos os convites que ainda não receberam ou ficaram com o link desativado — não só os
          // desta edição.
          const pendentes: SolicitarCotacaoFornecedor[] = this.cotacoes
            .filter(c => this.cotPrecisaEnvio(c, produtosAlterados))
            .map(c => {
              const info = this.nomesFornecedor.get(c.idFornecedor);
              return {
                idCotacaoFornecedor: c.idCotacaoFornecedor,
                nome: info?.name || nomeFornecedorFallback(c.idFornecedor),
                email: info?.email?.trim() || null,
                cadastroIndisponivel: !info?.carregado,
                reenvio: !!c.datetimeEnvio,
              };
            });

          if (pendentes.length === 0) { onDone(); return; }

          const ref = this.dialog.open(SolicitarCotacaoDialogComponent, {
            data: { fornecedores: pendentes },
            autoFocus: false,
            maxWidth: '94vw',
          });

          // A decisão e o envio ficam DENTRO do save: se liberássemos o save antes, o card
          // fecharia, esta etapa seria destruída e a inscrição do modal morreria com ela —
          // o e-mail nunca sairia.
          this.subs.add(
            ref.afterClosed().subscribe((ids: number[] | undefined) => {
              if (!ids?.length) { onDone(); return; }
              this.enviarSolicitacoes(idCompra, ids, onDone, produtosAlterados);
            }),
          );
        },
        error: () => onDone(),
      }),
    );
  }

  /** Nunca recebeu, ou recebeu e o link foi (neste save ou antes) desativado pela alteração de produtos. */
  private cotPrecisaEnvio(c: CotacaoFornecedorStruct, produtosAlterados: boolean): boolean {
    return !c.datetimeEnvio || !!c.isLinkDesativado || produtosAlterados;
  }

  /** Envia o formulário para os convites confirmados no modal. */
  private enviarSolicitacoes(idCompra: number, ids: number[], onDone: () => void, produtosAlterados: boolean): void {
    const envios = ids
      .map(id => this.cotacoes.find(c => c.idCotacaoFornecedor === id))
      .filter((c): c is CotacaoFornecedorStruct => !!c && this.cotPrecisaEnvio(c, produtosAlterados))
      .map(cotacao => ({ cotacao, info: this.nomesFornecedor.get(cotacao.idFornecedor) }))
      .filter(d => !!d.info?.email?.trim());

    if (envios.length === 0) { onDone(); return; }

    this.cotSendingId = String(envios[0].cotacao.idCotacaoFornecedor);
    this.subs.add(
      this.automacaoService.idsUsuarioDosPerfis(TipoOcorrenciaCompraEnum.EnvioCotacao).pipe(
        switchMap(idsUsuarioPerfil => forkJoin(envios.map(({ cotacao, info }) => this.compraCotacaoService.enviarCotacaoFornecedor(
          idCompra, cotacao.idCotacaoFornecedor, info!.email!.trim(), info?.name, info?.documento ?? undefined, idsUsuarioPerfil,
        )))),
      ).subscribe({
        next: (respostas) => {
          this.cotSendingId = null;
          respostas.forEach(res => { if (res.cotacao) this.replaceCotacao(res.cotacao); });
          if (respostas.some(res => res.isError || !res.cotacao)) {
            this.alertService.show('Erro', 'Não foi possível enviar o formulário a todos os fornecedores.', AlertType.error);
          } else {
            this.alertService.show('Sucesso',
              envios.length === 1
                ? 'Formulário de cotação enviado ao fornecedor.'
                : `Formulário de cotação enviado a ${envios.length} fornecedores.`,
              AlertType.success);
          }
          onDone();
        },
        error: () => {
          this.cotSendingId = null;
          this.alertService.show('Erro', 'Não foi possível enviar o formulário de cotação.', AlertType.error);
          onDone();
        },
      }),
    );
  }

  cotRemoveSupplier(s: CotacaoSupplier): void {
    if (!this.card.idCompra) return;

    const ref = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Remover fornecedor',
        description: `Deseja remover ${s.name} da cotação? O pedido montado será zerado e você precisará montá-lo de novo. É possível convidar este fornecedor novamente depois.`,
        buttonConfirm: 'Remover',
        buttonClose: 'Cancelar',
      },
    });

    ref.afterClosed().subscribe(result => {
      if (!result?.confirm || !this.card.idCompra) return;

      this.subs.add(
        this.compraCotacaoService.removerCotacaoFornecedor(this.card.idCompra, s.idCotacaoFornecedor).subscribe({
          next: (res) => {
            if (res.isError) {
              this.alertService.show('Erro', res.errorDescription || 'Não foi possível remover o fornecedor.', AlertType.error);
              return;
            }
            this.cotacoes = this.cotacoes.filter(item => item.idCotacaoFornecedor !== s.idCotacaoFornecedor);
            delete this.cotParcelasState[s.id];
            if (this.expandedSupplierId === s.id) this.expandedSupplierId = null;
            this.aplicarCotacoes();
            // Excluir um fornecedor zera o pedido montado e o total (o backend também zera valorTotal/preços
            // aplicados na compra). O comprador remonta a partir dos fornecedores restantes e salva de novo.
            // Já gravado: não suja o card; o quadro recarrega a coluna ao fechar.
            this.cotSelection = [];
            this.cotHasSelection = false;
            this.card.value = formatKanbanCardValue(0);
            this.alertService.show('Sucesso', 'Fornecedor removido. Pedido montado zerado.', AlertType.success);
          },
          error: () => this.alertService.show('Erro', 'Não foi possível remover o fornecedor.', AlertType.error),
        }),
      );
    });
  }

  cotToggleExpand(id: string): void {
    this.expandedSupplierId = this.expandedSupplierId === id ? null : id;
    if (this.expandedSupplierId === id) this.cotEnsureParcelas(id);
  }

  // ----- Parcelas (condição de pagamento por fornecedor) -----
  // A mecânica vive em CotacaoParcelasEditorComponent; aqui só o estado por fornecedor.
  private cotEnsureParcelas(id: string): void {
    if (this.cotParcelasState[id]) return;
    const cotacao = this.cotacoes.find(c => String(c.idCotacaoFornecedor) === id);
    this.cotParcelasState[id] = estadoParcelasDaCotacao(this.dataBaseParcelas, cotacao);
  }

  /** Precedência das parcelas: override da matriz > estado local editado > persistido. */
  private cotParcelasRequest(
    cotacao: CotacaoFornecedorStruct,
    override?: CotacaoParcela[] | null,
  ): AtualizarCotacaoFornecedorParcelaRequest[] | null {
    const st = this.cotParcelasState[String(cotacao.idCotacaoFornecedor)];
    const parcelas = override ?? st?.parcelas ?? mapParcelasCotacao(this.dataBaseParcelas, cotacao.parcelas);
    return parcelasParaRequest(this.dataBaseParcelas, parcelas);
  }

  /**
   * Persiste as condições de pagamento editadas de todos os fornecedores com alteração pendente.
   * Vivem em outro endpoint (CotacaoFornecedor) — o bloco de parcelas não tem botão próprio,
   * é salvo junto com o card.
   */
  private salvarCondicoesPagamento(idCompra: number, onDone: () => void, onError?: () => void): void {
    const pendentes = this.cotSuppliers
      .filter(s => this.cotCondicoesPagamentoAlteradas(s))
      .map(s => ({ s, cotacaoAtual: this.cotacoes.find(c => c.idCotacaoFornecedor === s.idCotacaoFornecedor) }))
      .filter((p): p is { s: CotacaoSupplier; cotacaoAtual: CotacaoFornecedorStruct } => !!p.cotacaoAtual);

    if (pendentes.length === 0) { onDone(); return; }

    const chamadas = pendentes.map(({ s, cotacaoAtual }) => {
      const st = this.cotParcelasState[s.id];
      const cotacao: CotacaoFornecedorStruct = st
        ? { ...cotacaoAtual, condicaoPagamento: st.expr.trim() || null }
        : cotacaoAtual;
      return this.compraCotacaoService.atualizarCotacaoFornecedor(idCompra, s.idCotacaoFornecedor, this.buildCotacaoRequest(cotacao, false));
    });

    this.subs.add(
      forkJoin(chamadas).subscribe({
        next: (respostas) => {
          const falha = respostas.find(r => r.isError || !r.cotacao);
          if (falha) {
            this.alertService.show('Erro', falha.errorDescription || 'Não foi possível salvar as condições de pagamento.', AlertType.error);
            onError?.();
            return;
          }
          respostas.forEach((res, i) => {
            if (res.cotacao) this.replaceCotacao(res.cotacao);
            this.cotMeiosPagamentoAlterados[pendentes[i].s.id] = false;
          });
          onDone();
        },
        error: () => {
          this.alertService.show('Erro', 'Não foi possível salvar as condições de pagamento.', AlertType.error);
          onError?.();
        },
      }),
    );
  }

  // Gera/garante o link do portal para ESTE fornecedor e copia — para enviar por outro canal.
  cotCopiarLink(s: CotacaoSupplier): void {
    if (!this.card.idCompra) return;
    this.subs.add(
      this.compraCotacaoService.gerarLinkCotacao(this.card.idCompra, s.idCotacaoFornecedor, s.documento ?? undefined).subscribe({
        next: (res) => {
          if (res.isError || !res.link) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível gerar o link.', AlertType.error);
            return;
          }
          this.cotCopiarParaClipboard(this.cotacaoUrlPublica(res.link));
          this.alertService.show('Sucesso', 'Link do fornecedor copiado para a área de transferência.', AlertType.success);
        },
        error: () => this.alertService.show('Erro', 'Não foi possível gerar o link.', AlertType.error),
      }),
    );
  }

  private cotCopiarParaClipboard(texto: string): void {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(texto).catch(() => this.cotCopiarFallback(texto));
      return;
    }
    this.cotCopiarFallback(texto);
  }

  private cotacaoUrlPublica(link: string): string {
    try {
      const urlGerada = new URL(link);
      const urlPortal = new URL(environment.urlPortal);
      urlGerada.protocol = urlPortal.protocol;
      urlGerada.hostname = urlPortal.hostname;
      urlGerada.port = urlPortal.port;
      return urlGerada.toString();
    } catch {
      return link;
    }
  }

  private cotCopiarFallback(texto: string): void {
    const ta = document.createElement('textarea');
    ta.value = texto;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch { /* ignore */ }
    ta.remove();
  }

  cotSendSupplier(s: CotacaoSupplier): void {
    if (!this.card.idCompra) return;

    const ref = this.dialog.open(EnviarCotacaoEmailDialogComponent, {
      width: '510px',
      maxWidth: '94vw',
      autoFocus: false,
      data: { nomeFornecedor: s.name, destinatarioPadrao: s.email ?? '' },
    });

    ref.afterClosed().subscribe((destinatario: string | undefined) => {
      if (!destinatario || !this.card.idCompra) return;
      const idCompra = this.card.idCompra;
      this.cotSendingId = s.id;

      const enviar = () => {
        this.subs.add(
          this.automacaoService.idsUsuarioDosPerfis(TipoOcorrenciaCompraEnum.EnvioCotacao).pipe(
            switchMap(idsUsuarioPerfil => this.compraCotacaoService.enviarCotacaoFornecedor(
              idCompra, s.idCotacaoFornecedor, destinatario, s.name, s.documento ?? undefined, idsUsuarioPerfil)),
          ).subscribe({
            next: (res) => {
              this.cotSendingId = null;
              if (res.isError || !res.cotacao) {
                this.alertService.show('Erro', res.errorDescription || 'Não foi possível enviar a cotação.', AlertType.error);
                return;
              }
              this.replaceCotacao(res.cotacao);
              this.alertService.show('Sucesso', `Cotação salva e enviada para ${destinatario}.`, AlertType.success);
            },
            error: () => {
              this.cotSendingId = null;
              this.alertService.show('Erro', 'Não foi possível enviar a cotação.', AlertType.error);
            },
          }),
        );
      };

      // Salva as edições do fornecedor (condição de pagamento / parcelas) antes de enviar, mesmo que
      // o usuário não tenha clicado em "Salvar fornecedor" — o e-mail e a UI refletem o estado atual.
      const cotacaoAtual = this.cotacoes.find(c => c.idCotacaoFornecedor === s.idCotacaoFornecedor);
      if (!cotacaoAtual) { enviar(); return; }
      const st = this.cotParcelasState[s.id];
      const cotacao: CotacaoFornecedorStruct = st
        ? { ...cotacaoAtual, condicaoPagamento: st.expr.trim() || null }
        : cotacaoAtual;

      this.subs.add(
        this.compraCotacaoService.atualizarCotacaoFornecedor(idCompra, s.idCotacaoFornecedor, this.buildCotacaoRequest(cotacao, false)).subscribe({
          next: (res) => {
            if (res.isError || !res.cotacao) {
              this.cotSendingId = null;
              this.alertService.show('Erro', res.errorDescription || 'Não foi possível salvar a cotação antes de enviar.', AlertType.error);
              return;
            }
            this.replaceCotacao(res.cotacao);
            this.cotMeiosPagamentoAlterados[s.id] = false;
            enviar();
          },
          error: () => {
            this.cotSendingId = null;
            this.alertService.show('Erro', 'Não foi possível salvar a cotação antes de enviar.', AlertType.error);
          },
        }),
      );
    });
  }

  /**
   * Única montagem de AtualizarCotacaoFornecedorRequest do módulo: recebe a cotação completa (já
   * mesclada) e serializa, para nenhum campo depender de preservação manual em cada chamador.
   */
  private buildCotacaoRequest(
    cotacao: CotacaoFornecedorStruct,
    marcarComoRespondida: boolean,
    origem?: OrigemCotacaoEnum,
    parcelasOverride?: CotacaoParcela[] | null,
  ): AtualizarCotacaoFornecedorRequest {
    return {
      condicaoPagamento: cotacao.condicaoPagamento ?? null,
      idTipoFrete: cotacao.idTipoFrete ?? null,
      idOrigemCotacao: origem ?? cotacao.idOrigemCotacao ?? null,
      valorFrete: cotacao.valorFrete ?? null,
      validadeDias: cotacao.validadeDias ?? null,
      valorEncargos: cotacao.valorEncargos ?? null,
      observacoes: cotacao.observacoes ?? null,
      marcarComoRespondida,
      parcelas: this.cotParcelasRequest(cotacao, parcelasOverride),
      itens: (cotacao.itens || []).map(item => ({
        idCompraItem: item.idCompraItem,
        valorUnitario: item.valorUnitario ?? null,
        marcaOfertada: item.marcaOfertada ?? null,
        prazoEntregaDias: item.prazoEntregaDias ?? null,
        quantidade: item.quantidade ?? null,
        disponibilidade: item.disponibilidade ?? null,
        valorIpi: item.valorIpi ?? null,
        valorIcmsSt: item.valorIcmsSt ?? null,
        isNaoCotado: item.isNaoCotado,
        observacoes: item.observacoes ?? null,
      })),
    };
  }

  private replaceCotacao(cotacao: CotacaoFornecedorStruct): void {
    this.cotacoes = this.cotacoes.map(item => item.idCotacaoFornecedor === cotacao.idCotacaoFornecedor ? cotacao : item);
    if (!this.cotacoes.some(item => item.idCotacaoFornecedor === cotacao.idCotacaoFornecedor)) {
      this.cotacoes = [...this.cotacoes, cotacao];
    }
    const id = String(cotacao.idCotacaoFornecedor);
    if (this.cotParcelasState[id]) {
      // A forma de pagamento agora é gravada (idFormaPagamento): o persistido é a fonte.
      this.cotParcelasState[id] = {
        expr: cotacao.condicaoPagamento || '',
        parcelas: mapParcelasCotacao(this.dataBaseParcelas, cotacao.parcelas),
      };
    }
    const nomes = new Map(this.cotSuppliers.map(s => [s.idFornecedor, { name: s.name, initials: s.initials, color: s.color }]));
    this.aplicarCotacoes();
    this.cotSuppliers = this.cotSuppliers.map(s => {
      const atual = nomes.get(s.idFornecedor);
      return atual ? { ...s, ...atual } : s;
    });
  }

  // ----- Cotação por planilha -----
  cotToggleSheet(): void {
    this.cotSheetOpen = !this.cotSheetOpen;
    if (!this.cotSheetOpen) {
      this.cotSheetFileName = '';
      this.cotSheetSupplierId = '';
    }
  }

  cotPickSheet(): void {
    this.cotSheetInput?.nativeElement.click();
  }

  cotOnSheetFile(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    this.cotProcessSheetFile(file);
  }

  cotOnSheetDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    this.cotProcessSheetFile(file);
  }

  private cotProcessSheetFile(file: File | undefined): void {
    if (!file) return;

    const nome = file.name.toLowerCase();
    if (!nome.endsWith('.csv') && !nome.endsWith('.xlsx') && !nome.endsWith('.xlsm')) {
      this.cotClearSheetFile();
      this.alertService.show('Atenção', 'Apenas arquivos .csv, .xlsx ou .xlsm são permitidos.', AlertType.warning);
      return;
    }

    if (file.size > this.cotSheetMaxSizeMb * 1024 * 1024) {
      this.cotClearSheetFile();
      this.alertService.show('Atenção', `O arquivo deve ter no máximo ${this.cotSheetMaxSizeMb}MB.`, AlertType.warning);
      return;
    }

    this.cotSheetFile = file;
    this.cotSheetFileName = file.name;
  }

  cotClearSheetFile(): void {
    this.cotSheetFile = null;
    this.cotSheetFileName = '';
    if (this.cotSheetInput) this.cotSheetInput.nativeElement.value = '';
  }

  get cotCanImport(): boolean {
    return !!this.cotSheetSupplierId && !!this.cotSheetFileName;
  }

  // Baixa o modelo no formato escolhido, gerado no backend com o MESMO layout dos documentos
  // enviados por e-mail (planilha/PDF/CSV) — download e e-mail ficam idênticos por formato.
  cotBaixarModelo(formato: 'csv' | 'xlsx' | 'pdf'): void {
    if (!this.card.idCompra) return;
    this.subs.add(
      this.compraCotacaoService.baixarModeloCotacao(this.card.idCompra, formato).subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `cotacao-modelo-${this.card.id.replace('#', '')}.${formato}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        error: () => this.alertService.show('Erro', 'Não foi possível baixar o modelo.', AlertType.error),
      }),
    );
  }

  async cotSubmitImport(): Promise<void> {
    if (!this.cotCanImport || this.cotImportando || !this.cotSheetFile) return;
    const supplier = this.cotSuppliers.find(s => s.id === this.cotSheetSupplierId);
    if (!supplier || !this.card.idCompra) return;
    const cotacao = this.cotacoes.find(c => c.idCotacaoFornecedor === supplier.idCotacaoFornecedor);
    if (!cotacao) return;

    /* Capturados agora: o nome do arquivo é limpo por cotToggleSheet/cotClearSheetFile,
       então lê-lo dentro do callback assíncrono traria string vazia para o toast. */
    const idCompra = this.card.idCompra;
    const nomeFornecedor = supplier.name || 'Fornecedor';
    const nomeArquivo = this.cotSheetFileName;

    this.cotImportando = true;
    let cotacaoAtualizada: CotacaoFornecedorStruct | null;
    try {
      cotacaoAtualizada = await this.aplicarArquivoCotacao(cotacao, this.cotSheetFile);
    } catch {
      cotacaoAtualizada = null;
      this.alertService.show('Erro', 'Não foi possível ler o arquivo. Confira se é o modelo da cotação.', AlertType.error);
    }
    // Arquivo não reconhecido (o parser já avisou): nada é gravado.
    if (!cotacaoAtualizada) {
      this.cotImportando = false;
      return;
    }

    const request = this.buildCotacaoRequest(cotacaoAtualizada, true, OrigemCotacaoEnum.Planilha);
    this.subs.add(
      this.compraCotacaoService.atualizarCotacaoFornecedor(idCompra, supplier.idCotacaoFornecedor, request).subscribe({
        next: (res) => {
          this.cotImportando = false;
          if (res.isError || !res.cotacao) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível importar a cotação.', AlertType.error);
            return;
          }
          this.replaceCotacao(res.cotacao);
          this.cotSuppliers = this.cotSuppliers.map(s =>
            s.id === supplier.id
              ? { ...s, status: 'recebida', when: 'importado agora' }
              : s);
          this.alertService.show(
            'Sucesso',
            `Cotação de ${nomeFornecedor} importada a partir de ${nomeArquivo}. Já aparece na matriz.`,
            AlertType.success,
          );
          this.cotSheetOpen = false;
          this.cotSheetFile = null;
          this.cotSheetFileName = '';
          this.cotSheetSupplierId = '';
          if (this.cotSheetInput) this.cotSheetInput.nativeElement.value = '';
        },
        error: () => {
          this.cotImportando = false;
          this.alertService.show('Erro', 'Não foi possível importar a cotação.', AlertType.error);
        },
      }),
    );
  }

  /**
   * Aplica o arquivo preenchido pelo fornecedor sobre a cotação. Devolve `null` (com toast de erro)
   * quando o arquivo não é reconhecido como o modelo da cotação — o import é abortado.
   */
  private async aplicarArquivoCotacao(cotacao: CotacaoFornecedorStruct, file: File): Promise<CotacaoFornecedorStruct | null> {
    const nome = file.name.toLowerCase();
    let rows: string[][];
    if (nome.endsWith('.xlsx') || nome.endsWith('.xlsm')) {
      rows = await this.parseXlsx(file);
    } else if (nome.endsWith('.csv')) {
      rows = this.parseCsv(await file.text());
    } else {
      this.alertService.show('Erro', 'Apenas arquivos .csv, .xlsx ou .xlsm são permitidos.', AlertType.error);
      return null;
    }

    const norm = (v: unknown) => String(v ?? '').trim().toLowerCase();

    // O preâmbulo (título, instruções) é ignorado: a tabela começa na linha de cabeçalho,
    // detectada por conter "Produto" e "SKU" (mesmo layout do modelo/planilha enviada).
    const headerIdx = rows.findIndex(r => r.map(norm).includes('produto') && r.map(norm).includes('sku'));
    if (headerIdx < 0) {
      this.alertService.show('Erro', 'Modelo não reconhecido. Baixe o modelo novamente e preencha as células.', AlertType.error);
      return null;
    }

    // Mapa coluna→índice a partir dos nomes do cabeçalho (robusto a reordenação).
    const head = rows[headerIdx].map(norm);
    const achar = (...termos: string[]) => head.findIndex(h => termos.some(t => h.includes(t)));
    const col = {
      produto: achar('produto'),
      sku: achar('sku'),
      preco: achar('preç', 'prec'),
      ipi: achar('ipi'),
      // "ST estimado por produto" (config) ou "ST" (padrão) ou ICMS ST.
      st: head.findIndex(h => h === 'st' || h.includes('st ') || h.includes('icms')),
      dispon: achar('dispon'),
      obs: achar('observ'),
    };

    // Índice por SKU só com chaves não-vazias: produtos avulsos (sem SKU) casariam
    // todos na chave "" e uma linha sem SKU (ex.: cabeçalho "Condições da proposta")
    // acabaria "batendo" com um produto avulso. Esses caem no fallback por nome.
    // Só produtos já salvos: o novo (sem idCompraItem) não está no modelo e, com o mesmo nome de
    // um salvo, tomaria o lugar dele no índice e a linha da planilha seria descartada.
    const produtosSalvos = this.products.filter(p => !!p.idCompraItem);
    const produtosPorSku = new Map<string, CardProduct>();
    for (const p of produtosSalvos) {
      const s = (p.sku || '').trim().toLowerCase();
      if (s) produtosPorSku.set(s, p);
    }
    const produtosPorNome = new Map(produtosSalvos.map(p => [p.name.trim().toLowerCase(), p]));
    const itens = [...(cotacao.itens || [])];
    let valorFrete = cotacao.valorFrete ?? null;
    let condicaoPagamento = cotacao.condicaoPagamento ?? null;
    let validadeDias = cotacao.validadeDias ?? null;
    let prazoGeral: number | null = null;
    let emCondicoes = false;
    let produtosLidos = 0;

    const cell = (row: string[], idx: number) => (idx >= 0 ? row[idx] : undefined);
    const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

    // Extrai (rótulo, valor) de uma linha da seção "Condições da proposta", robusto ao
    // layout: no CSV o par fica em A/B; na planilha (.xlsx) o rótulo fica em B e o valor
    // na célula mesclada C-E. Pega a 1ª célula preenchida como rótulo e a próxima como valor.
    const rotuloValor = (row: string[]): { rotulo: string; valor: string | undefined } => {
      const preenchidas = row.map((v, j) => ({ v, j })).filter(x => norm(x.v));
      if (preenchidas.length === 0) return { rotulo: '', valor: undefined };
      return { rotulo: norm(preenchidas[0].v), valor: preenchidas[1]?.v };
    };

    for (let i = headerIdx + 1; i < rows.length; i++) {
      const row = rows[i];
      // Início da seção "Condições da proposta" (em QUALQUER coluna — o layout muda entre
      // CSV e planilha). O texto é fixo no gerador, então casa pela frase inteira.
      if (!emCondicoes && row.some(c => semAcento(norm(c)).includes('condicoes da proposta'))) {
        emCondicoes = true;
        continue;
      }

      if (emCondicoes) {
        const { rotulo, valor } = rotuloValor(row);
        if (rotulo.includes('frete')) { const v = this.parseDecimal(valor); if (v != null) valorFrete = v; }
        else if (rotulo.includes('pagamento')) { condicaoPagamento = (valor?.trim() || null); }
        else if (rotulo.includes('validade')) { validadeDias = this.parseInteger(valor); }
        else if (rotulo.includes('prazo')) { prazoGeral = this.parseInteger(valor); }
        continue;
      }

      const sku = norm(cell(row, col.sku));
      const nome = norm(cell(row, col.produto));
      const produto = (sku ? produtosPorSku.get(sku) : undefined) || (nome ? produtosPorNome.get(nome) : undefined);
      if (!produto?.idCompraItem) continue;
      produtosLidos++;

      const valorUnitario = this.parseDecimal(cell(row, col.preco));
      const disponibilidade = this.parseDecimal(cell(row, col.dispon));
      const camposPlanilha = {
        // A planilha é a resposta do FORNECEDOR — só toca disponibilidade (o que ele tem).
        // quantidade (quanto o comprador decide pedir) não entra aqui de propósito: o backend
        // preserva o que já estava, ou usa a solicitada só na criação do item.
        disponibilidade: disponibilidade != null ? disponibilidade : produto.qty,
        valorUnitario,
        valorIpi: this.parseDecimal(cell(row, col.ipi)),
        valorIcmsSt: this.parseDecimal(cell(row, col.st)),
        observacoes: cell(row, col.obs)?.trim() || null,
        isNaoCotado: !(valorUnitario && valorUnitario > 0),
      };

      const idx = itens.findIndex(it => it.idCompraItem === produto.idCompraItem);
      if (idx >= 0) {
        itens[idx] = { ...itens[idx], ...camposPlanilha };
      } else {
        // Produto ainda sem entrada na cotação deste fornecedor (ex.: item avulso
        // adicionado à compra depois do envio) — cria a partir da planilha importada,
        // igual ao que a matriz de comparação já faz ao listar por this.products.
        itens.push({
          idCotacaoFornecedorItem: 0,
          idCompraItem: produto.idCompraItem,
          ...camposPlanilha,
        });
      }
    }

    if (produtosLidos === 0) {
      this.alertService.show('Erro', 'Nenhum produto da cotação foi encontrado no arquivo. Confira se é o modelo desta cotação.', AlertType.error);
      return null;
    }

    // Prazo de entrega é geral na proposta — aplica a todos os itens cotados.
    if (prazoGeral != null) {
      for (let i = 0; i < itens.length; i++) itens[i] = { ...itens[i], prazoEntregaDias: prazoGeral };
    }

    return { ...cotacao, valorFrete, condicaoPagamento, validadeDias, itens };
  }

  private parseCsv(text: string): string[][] {
    const clean = text.replace(/^﻿/, '');
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;

    for (let i = 0; i < clean.length; i++) {
      const ch = clean[i];
      const next = clean[i + 1];
      if (ch === '"' && quoted && next === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        quoted = !quoted;
      } else if (ch === ';' && !quoted) {
        row.push(cell);
        cell = '';
      } else if ((ch === '\n' || ch === '\r') && !quoted) {
        if (ch === '\r' && next === '\n') i++;
        row.push(cell);
        if (row.some(v => v.trim())) rows.push(row);
        row = [];
        cell = '';
      } else {
        cell += ch;
      }
    }

    row.push(cell);
    if (row.some(v => v.trim())) rows.push(row);
    return rows;
  }

  private async parseXlsx(file: File): Promise<string[][]> {
    const data = await readSheet(file);
    // Célula numérica vira texto pt-BR sem separador de milhar ("1234,5"): String(1234.5) daria
    // "1234.5", e parseDecimal (que remove o ponto de milhar) leria 12345.
    return data
      .map(row => row.map(cell => {
        if (cell == null) return '';
        if (typeof cell === 'number') return cell.toLocaleString('pt-BR', { useGrouping: false, maximumFractionDigits: 10 });
        return String(cell);
      }))
      .filter(row => row.some(v => v.trim()));
  }

  private parseDecimal(value: string | undefined): number | null {
    const normalized = (value || '').replace(/\./g, '').replace(',', '.').trim();
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  // Usado só para "dias" (validade/prazo). Aceita "15", "15 dias", "15,0 dias" — mas
  // rejeita datas ("29/07/2026") e listas ("30 60"), que não são uma contagem de dias.
  private parseInteger(value: string | undefined): number | null {
    const m = (value || '').trim().match(/^(\d+)(?:[.,]\d+)?\s*(?:dias?|d)?\.?$/i);
    return m ? parseInt(m[1], 10) : null;
  }

  cotOpenMatrix(): void {
    const products: MatrixProduct[] = this.products
      .filter(p => !!p.idCompraItem)
      .map(p => ({
        key: String(p.idCompraItem),
        idCompraItem: p.idCompraItem!,
        sku: p.sku,
        name: p.name,
        qty: p.qty,
        unit: p.unit,
      }));

    // Prazo, condição de pagamento e frete NÃO vêm aqui: são editáveis no painel da matriz e
    // ela os deriva de `offers`/`headers` para as linhas reagirem à edição na hora.
    const suppliers: MatrixSupplier[] = this.cotacoes.map(c => {
      const sup = this.cotSuppliers.find(s => s.idCotacaoFornecedor === c.idCotacaoFornecedor);
      return {
        id: String(c.idCotacaoFornecedor),
        idCotacaoFornecedor: c.idCotacaoFornecedor,
        idFornecedor: c.idFornecedor,
        initials: sup?.initials || initialsFromName(nomeFornecedorFallback(c.idFornecedor)),
        color: sup?.color || avatarColorFromSeed(String(c.idFornecedor)),
        name: sup?.name || nomeFornecedorFallback(c.idFornecedor),
        tag: c.datetimeResposta ? 'Respondido' : 'Pendente',
        respondido: !!c.datetimeResposta,
      };
    });

    if (!products.length || !suppliers.length) {
      this.alertService.show('Atenção', 'Adicione fornecedores e produtos à cotação antes de comparar.', AlertType.warning);
      return;
    }

    const offers: Record<string, Record<string, MatrixOffer>> = {};
    const encargos: Record<string, number | null> = {};
    const headers: Record<string, MatrixSupplierHeader> = {};
    for (const c of this.cotacoes) {
      const sid = String(c.idCotacaoFornecedor);
      offers[sid] = {};
      for (const item of (c.itens || [])) {
        const produto = this.products.find(p => p.idCompraItem === item.idCompraItem);
        offers[sid][String(item.idCompraItem)] = {
          // qty = quantidade que o comprador decide pedir DESTE fornecedor (editável na
          // matriz); default à solicitada quando ainda não há decisão específica.
          qty: item.quantidade ?? produto?.qty ?? 0,
          disponibilidade: item.disponibilidade ?? null,
          // Número como veio do backend (até 4 casas): formatar com 2 aqui fazia a matriz regravar arredondado.
          price: item.valorUnitario != null && item.valorUnitario > 0 ? item.valorUnitario : null,
          prazoEntregaDias: item.prazoEntregaDias ?? null,
          valorIpi: item.valorIpi ?? null,
          valorIcmsSt: item.valorIcmsSt ?? null,
          marcaOfertada: item.marcaOfertada ?? null,
          observacoes: item.observacoes ?? null,
          alternativaDescricao: item.alternativaDescricao ?? null,
          alternativaMarca: item.alternativaMarca ?? null,
          alternativaValorUnitario: item.alternativaValorUnitario ?? null,
        };
      }
      encargos[sid] = c.valorEncargos != null && c.valorEncargos > 0 ? c.valorEncargos : null;
      headers[sid] = {
        // Reaproveita o mesmo estado de parcelas da etapa: a expressão é a condição de
        // pagamento e a grade é derivada dela quando não há parcelas persistidas.
        pagamento: estadoParcelasDaCotacao(this.dataBaseParcelas, c),
        idTipoFrete: c.idTipoFrete ?? null,
        valorFrete: c.valorFrete ?? null,
        validadeDias: c.validadeDias ?? null,
        observacoes: c.observacoes ?? '',
      };
    }

    const ref = this.dialog.open(ComparisonMatrixDialogComponent, {
      data: {
        criterio: this.cotCriterio,
        products,
        suppliers,
        offers,
        encargos,
        headers,
        tiposFrete: this.tiposFrete,
        formasPagamento: this.formasPagamento,
        dataBaseParcelas: this.dataBaseParcelas,
        selections: this.selecoesParaMatriz(),
      },
      // Abre em 96vw × 92vh; o botão do cabeçalho maximiza para a tela cheia (mesma moldura do vínculo NF × pedido).
      width: '96vw',
      height: '92vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      autoFocus: false,
    });
    ref.afterClosed().subscribe((result: ComparisonMatrixResult | null) => {
      if (!result) return;
      // Edições e seleção da matriz são gravadas na hora (abaixo); só o critério depende do
      // "Salvar" do card, então só a troca dele suja o card.
      if (result.criterio !== this.cotCriterio) this.dirty.emit();
      this.cotCriterio = result.criterio;
      this.emitirCriterio();
      // Persiste as edições nas cotações e só então aplica a seleção vencedora à compra (preços
      // nos itens + valor total): com falha em alguma edição, a seleção não é aplicada.
      this.persistirEdicoesMatriz(result.edits, () => {
        // A matriz é o único lugar onde se escolhe fornecedor por item, então a seleção que ela
        // devolve vale para QUALQUER critério — o critério só alimenta a sugestão lá dentro.
        if (result.selection?.length) {
          this.cotSelection = this.enriquecerSelecaoDaMatriz(result.selection);
          this.cotHasSelection = true;
          this.cotSelecaoAplicadaNaMatriz = true;
        } else {
          // Seleção limpa na matriz: deixa de ser a decisão vigente e o "Pedido montado" volta ao
          // cálculo do critério (vazio no modo manual).
          this.cotSelection = [];
          this.cotSelecaoAplicadaNaMatriz = false;
          this.recalcularSelecaoCotacao();
        }
        this.persistirSelecaoCotacao();
      });
    });
  }

  /**
   * Funde a edição da matriz numa cópia da cotação: o que ela edita sobrescreve, o resto é
   * preservado do struct original.
   */
  private aplicarEdicaoNaCotacao(
    cotacao: CotacaoFornecedorStruct | undefined,
    edit: MatrixSupplierEdit,
  ): CotacaoFornecedorStruct {
    const base: CotacaoFornecedorStruct = cotacao ?? {
      idCotacaoFornecedor: edit.idCotacaoFornecedor,
      idCompra: this.card.idCompra ?? 0,
      idFornecedor: 0,
      itens: [],
    };
    const editados = new Map(edit.itens.map(i => [i.idCompraItem, i]));

    const itensExistentes: CotacaoFornecedorItemStruct[] = (base.itens || []).map(item => {
      const e = editados.get(item.idCompraItem);
      if (!e) return item;
      editados.delete(item.idCompraItem);
      return {
        ...item,
        valorUnitario: e.valorUnitario,
        quantidade: e.quantidade,
        disponibilidade: e.disponibilidade,
        prazoEntregaDias: e.prazoEntregaDias,
        marcaOfertada: e.marcaOfertada,
        valorIpi: e.valorIpi,
        valorIcmsSt: e.valorIcmsSt,
        observacoes: e.observacoes,
        isNaoCotado: e.valorUnitario == null,
      };
    });

    // Itens que o fornecedor não tinha na cotação e o comprador preencheu na matriz.
    const itensNovos: CotacaoFornecedorItemStruct[] = [...editados.values()]
      .filter(e => e.valorUnitario != null || e.quantidade != null)
      .map(e => ({
        idCotacaoFornecedorItem: 0,
        idCompraItem: e.idCompraItem,
        valorUnitario: e.valorUnitario,
        quantidade: e.quantidade,
        disponibilidade: e.disponibilidade,
        prazoEntregaDias: e.prazoEntregaDias,
        marcaOfertada: e.marcaOfertada,
        valorIpi: e.valorIpi,
        valorIcmsSt: e.valorIcmsSt,
        observacoes: e.observacoes,
        isNaoCotado: e.valorUnitario == null,
      }));

    return {
      ...base,
      condicaoPagamento: edit.condicaoPagamento,
      idTipoFrete: edit.idTipoFrete,
      valorFrete: edit.valorFrete,
      validadeDias: edit.validadeDias,
      valorEncargos: edit.valorEncargos,
      observacoes: edit.observacoes,
      itens: [...itensExistentes, ...itensNovos],
    };
  }

  /** Seleção vigente no formato da matriz: idCompraItem → idCotacaoFornecedor (ambos como string). */
  private selecoesParaMatriz(): Record<string, string> {
    const selecoes: Record<string, string> = {};
    for (const sel of this.cotSelection) {
      if (sel.idCompraItem == null || sel.idFornecedorSel == null) continue;
      const cotacao = this.cotacoes.find(c => c.idFornecedor === sel.idFornecedorSel);
      if (cotacao) selecoes[String(sel.idCompraItem)] = String(cotacao.idCotacaoFornecedor);
    }
    return selecoes;
  }

  /** Aplica as edições da matriz sobre a cotação e persiste via `buildCotacaoRequest`. */
  private persistirEdicoesMatriz(edits: MatrixSupplierEdit[], afterApply?: () => void): void {
    if (!this.card.idCompra || !edits.length) { afterApply?.(); return; }
    const idCompra = this.card.idCompra;

    const calls = edits.map(edit => {
      const cotacao = this.cotacoes.find(c => c.idCotacaoFornecedor === edit.idCotacaoFornecedor);
      const mesclada = this.aplicarEdicaoNaCotacao(cotacao, edit);
      // Só a edição do comprador troca a origem para Manual — `marcarComoRespondida` sozinho
      // reescreveria a proveniência de uma cotação que veio do portal ou da planilha.
      const origem = edit.ajustadoPeloComprador ? OrigemCotacaoEnum.Manual : undefined;
      const request = this.buildCotacaoRequest(mesclada, edit.marcarComoRespondida, origem, edit.parcelas);
      return this.compraCotacaoService.atualizarCotacaoFornecedor(idCompra, edit.idCotacaoFornecedor, request);
    });

    this.savingChange.emit(true);
    this.subs.add(
      forkJoin(calls).subscribe({
        next: (respostas) => {
          this.savingChange.emit(false);
          const falhas: string[] = [];
          respostas.forEach((res, i) => {
            if (!res.isError && res.cotacao) {
              this.replaceCotacao(res.cotacao);
              return;
            }
            const cotacao = this.cotacoes.find(c => c.idCotacaoFornecedor === edits[i].idCotacaoFornecedor);
            const nome = cotacao ? this.cotNomeFornecedor(cotacao) : 'Fornecedor';
            falhas.push(`${nome}: ${res.errorDescription || 'não foi possível salvar.'}`);
          });
          // Falha parcial não é sucesso: o que gravou fica, mas a seleção só é aplicada com tudo gravado.
          if (falhas.length) {
            this.alertService.show(
              'Erro',
              `Nem todas as alterações da matriz foram salvas e a seleção não foi aplicada. ${falhas.join(' ')}`,
              AlertType.error,
            );
            return;
          }
          this.alertService.show('Sucesso', 'Cotações atualizadas pela matriz de comparação.', AlertType.success);
          afterApply?.();
        },
        error: () => {
          this.savingChange.emit(false);
          this.alertService.show('Erro', 'Não foi possível salvar as alterações da matriz.', AlertType.error);
        },
      }),
    );
  }
}
